import React, { useEffect, useState, useContext, useMemo } from 'react';

import { useHistory, useLocation } from 'react-router-dom';

import { Grid, Tab, Tabs } from '@mui/material';

import { useScreen, useGlobalApp } from 'hooks';
import UserRoleContext from 'context/user-role/context';
import { isAllowedByOffice } from 'context/user-role/helpers';

import type { OptionTab } from './models/option-tab';

import { doNotShowTab, doNotShowTabBySome, TAB_GROUPS } from './constants';
import {
  findTabsByCurrentPath,
  getCurrentOption,
  propagateExclusiveByOfficeToTabs,
} from './helpers';

const TabsNavigator = () => {
  const history = useHistory();
  const location = useLocation();
  const { isAccessDeniedByScreen } = useScreen();
  const { accessByMenu } = useContext(UserRoleContext);
  const { investmentOfficeId, isSuperAdmin } = useGlobalApp();

  const [value, setValue] = useState(0);

  const tabs = useMemo(() => {
    const foundTabs = findTabsByCurrentPath(location.pathname);
    return propagateExclusiveByOfficeToTabs(foundTabs, accessByMenu);
  }, [location.pathname, accessByMenu]);

  const visibleTabs = useMemo(() => {
    return tabs.filter((tab) => {
      if (isAccessDeniedByScreen(tab.screenKey || '')) {
        return false;
      }

      if (isSuperAdmin) {
        if (tab.exclusiveByOffice?.length) {
          return tab.exclusiveByOffice.includes(investmentOfficeId);
        }
        return true;
      }

      return isAllowedByOffice(tab.exclusiveByOffice, investmentOfficeId);
    });
  }, [tabs, isSuperAdmin, investmentOfficeId, isAccessDeniedByScreen]);

  const processChangeTab = (optionTabsParam: OptionTab[], newValue: number) => {
    const itemTab = optionTabsParam.find(
      (item) => item.path === location.pathname,
    );
    const valueTab = optionTabsParam.find((item) => item.value === newValue);

    if (itemTab && valueTab) {
      history.push(valueTab.path);
      setValue(newValue);
      return true;
    }
    return false;
  };

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    for (const statusChangeTab of TAB_GROUPS) {
      if (processChangeTab(statusChangeTab, newValue)) {
        return;
      }
    }
  };

  useEffect(() => {
    const currentOption = getCurrentOption(location.pathname);

    if (!currentOption) return;
    if (currentOption.value !== value) {
      setValue(currentOption.value);
    }
  }, [location, value]);

  if (doNotShowTab.includes(location.pathname)) return null;
  if (doNotShowTabBySome.some((path) => location.pathname.includes(path)))
    return null;

  return (
    <Grid item xs={12} md={11}>
      <Tabs
        value={value}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
        onChange={handleChange}
      >
        {visibleTabs.map((tab) => (
          <Tab
            data-testid="tab"
            label={tab.name || ''}
            key={tab.value}
            value={tab.value}
          />
        ))}
      </Tabs>
    </Grid>
  );
};

export default TabsNavigator;
